// ============================================================
// Capa de acceso a datos (persistencia) del módulo de Riesgo
// Psicosocial: evaluaciones, unidades, códigos de acceso, resultados y
// programa de prevención. Mismo patrón que gestion-tecnica-estres-termico.ts:
// cada función desestructura `{ data, error }` de Supabase y, si hay
// error, lanza. `PostgrestError` no es una instancia de `Error`, así que
// se relanza como `new Error(...)`. Los mensajes de las funciones de la
// base (RAISE EXCEPTION) ya vienen en español y llegan tal cual al
// usuario; de los errores de restricción de la base solo se traducen
// dos casos conocidos (violación de RLS y valor duplicado) — el resto
// pasa sin cambios.
//
// CITA REAL (fuente): los nombres de tablas, columnas y funciones RPC
// salen de las migraciones ya aplicadas y verificadas con autotests:
// 20260920000000 (esquema base: assessments, units, access_codes,
// responses), 20260920000100 (permisos por rol), 20260920000200
// (bucket privado de evidencia), 20260920000300 (programa y
// seguimiento), 20260920000500 (fecha límite, plazo de conservación en
// días y contacto del responsable de datos personales) y 20260920000600
// (resultados guardados por unidad, apertura/cierre atómico y borrado
// programado de respuestas individuales).
//
// DECISIÓN PROPIA (implementación, no de la fuente — pendiente de
// validar con Kevin):
//   - El saneado de nombres de archivo en `buildEvidencePath` (qué
//     caracteres se permiten, cómo se recorta la longitud) es criterio
//     propio; la base solo exige que `storage_path` sea único y que
//     `file_name` no esté vacío ni contenga '/'.
//   - Las validaciones previas en el cliente (headcount, formato de
//     hash, tamaño/tipo de archivo, reason no vacío, rango de
//     retentionDays, formato de contactEmail/contactPhone/dueAt) son un
//     espejo de lo que la base ya valida — se repiten aquí para dar un
//     mensaje de error más rápido y claro, no porque la base no las
//     tenga.
//   - El rango de 1 a 30 días de retentionDays es DECISIÓN PROPIA (ver
//     la migración 20260920000500) — ninguna norma lo fija.
//   - El mínimo de respondientes por unidad para no suprimir sus
//     resultados (PSICOSOCIAL_MIN_GROUP_SIZE = 3, migración 20260925000000;
//     antes 5) lo validó Kevin — la guía no define este umbral. Las
//     evaluaciones cerradas antes del cambio conservan min_group = 5.
//   - Desde 20260925000000 la purga programada borra también los códigos
//     de acceso: después de la purga, fetchCodeStats devuelve 0/0.
//   - El cálculo de la distribución de resultados (bajo/medio/alto por
//     dimensión, subdimensión y global) vive ahora en SQL
//     (psychosocial_close_assessment, migración 20260920000600), no en
//     esta capa ni en la aplicación — mapResult() solo valida la forma
//     de lo que ya vino calculado.
//   - La URL firmada de evidencia dura 60 segundos, igual que el patrón
//     ya usado en incidentEvidence.ts para otros documentos privados.
//
// LIMITACIÓN CONOCIDA (de la migración 20260920000300): `registerEvidence`
// envía `fileSize` y `mimeType` tal como los declara el cliente al
// llamar a la función; la base no los contrasta contra el archivo
// realmente guardado en Storage.
//
// LIMITACIÓN CONOCIDA (de la migración 20260920000600): ningún usuario
// de la aplicación puede leer respuestas individuales — solo los
// resultados agregados guardados en psychosocial_results.
// ============================================================

import { supabase } from '../supabaseClient'
import { nombreSeguroStorage } from './nombre-storage'
import { PSICOSOCIAL_MIN_GROUP_SIZE } from './psicosocial-calculo'

export const MIN_RETENTION_DAYS = 1
export const MAX_RETENTION_DAYS = 30
export const DEFAULT_RETENTION_DAYS = 30

// ── Tipos ────────────────────────────────────────────────────────────────

export type AssessmentStatus = 'borrador' | 'abierta' | 'cerrada'

export interface PsicosocialAssessment {
  id: string
  companyId: string
  title: string
  instrumentId: string
  instrumentVersion: string
  itemCount: number
  status: AssessmentStatus
  openedAt: string | null
  closedAt: string | null
  createdAt: string
  dueAt: string | null
  retentionDays: number
  contactEmail: string | null
  contactPhone: string | null
  responsesPurgedAt: string | null
}

export interface PsicosocialUnit {
  id: string
  assessmentId: string
  name: string
  headcount: number
}

export interface UnitCodeStat {
  unitId: string
  unitName: string
  headcount: number
  codesTotal: number
  codesUsed: number
}

export type CycleStatus = 'abierto' | 'cerrado'

export interface ProgramCycle {
  id: string
  companyId: string
  programYear: number
  status: CycleStatus
  closedAt: string | null
  createdAt: string
}

export interface ProgramActivityRow {
  id: string
  cycleId: string
  activityNo: number
  plannedMonths: number[]
  numerator: number | null
  denominator: number | null
  notes: string | null
  updatedAt: string
}

export interface ProgramEvidenceRow {
  id: string
  cycleId: string
  activityId: string
  kind: string
  storagePath: string
  fileName: string
  fileSize: number
  mimeType: string
  uploadedAt: string
  voided: boolean
  voidedAt: string | null
  voidReason: string | null
}

// ── Helpers internos ────────────────────────────────────────────────────

function required<T>(value: T | null | undefined, field: string): T {
  if (value === null || value === undefined) {
    throw new Error(`Falta el campo obligatorio "${field}" en la fila recibida`)
  }
  return value
}

function friendlyMessage(error: { message: string; code?: string }): string {
  if (error.message.indexOf('row-level security') !== -1) return 'Sin permiso para realizar esta acción'
  if (error.message.indexOf('permission denied') !== -1) return 'Sin permiso para realizar esta acción'
  if (error.code === '23505') return 'Ya existe un registro con esos datos'
  return error.message
}

function throwIfError(error: { message: string; code?: string } | null): void {
  if (error) throw new Error(friendlyMessage(error))
}

// ── Mapeadores puros (fila cruda snake_case → objeto camelCase) ────────────

export function mapAssessment(row: any): PsicosocialAssessment {
  return {
    id: required(row.id, 'id'),
    companyId: required(row.company_id, 'company_id'),
    title: required(row.title, 'title'),
    instrumentId: required(row.instrument_id, 'instrument_id'),
    instrumentVersion: required(row.instrument_version, 'instrument_version'),
    itemCount: required(row.item_count, 'item_count'),
    status: required(row.status, 'status'),
    openedAt: row.opened_at ?? null,
    closedAt: row.closed_at ?? null,
    createdAt: required(row.created_at, 'created_at'),
    dueAt: row.due_at ?? null,
    retentionDays: required(row.retention_days, 'retention_days'),
    contactEmail: row.contact_email ?? null,
    contactPhone: row.contact_phone ?? null,
    responsesPurgedAt: row.responses_purged_at ?? null,
  }
}

export function mapUnit(row: any): PsicosocialUnit {
  return {
    id: required(row.id, 'id'),
    assessmentId: required(row.assessment_id, 'assessment_id'),
    name: required(row.name, 'name'),
    headcount: required(row.headcount, 'headcount'),
  }
}

export function mapCodeStat(row: any): UnitCodeStat {
  return {
    unitId: required(row.unit_id, 'unit_id'),
    unitName: required(row.unit_name, 'unit_name'),
    headcount: required(row.headcount, 'headcount'),
    codesTotal: required(row.codes_total, 'codes_total'),
    codesUsed: required(row.codes_used, 'codes_used'),
  }
}

export function mapCycle(row: any): ProgramCycle {
  return {
    id: required(row.id, 'id'),
    companyId: required(row.company_id, 'company_id'),
    programYear: required(row.program_year, 'program_year'),
    status: required(row.status, 'status'),
    closedAt: row.closed_at ?? null,
    createdAt: required(row.created_at, 'created_at'),
  }
}

export function mapActivity(row: any): ProgramActivityRow {
  return {
    id: required(row.id, 'id'),
    cycleId: required(row.cycle_id, 'cycle_id'),
    activityNo: required(row.activity_no, 'activity_no'),
    plannedMonths: required(row.planned_months, 'planned_months'),
    numerator: row.numerator ?? null,
    denominator: row.denominator ?? null,
    notes: row.notes ?? null,
    updatedAt: required(row.updated_at, 'updated_at'),
  }
}

export function mapEvidence(row: any): ProgramEvidenceRow {
  return {
    id: required(row.id, 'id'),
    cycleId: required(row.cycle_id, 'cycle_id'),
    activityId: required(row.activity_id, 'activity_id'),
    kind: required(row.kind, 'kind'),
    storagePath: required(row.storage_path, 'storage_path'),
    fileName: required(row.file_name, 'file_name'),
    fileSize: required(row.file_size, 'file_size'),
    mimeType: required(row.mime_type, 'mime_type'),
    uploadedAt: required(row.uploaded_at, 'uploaded_at'),
    voided: required(row.voided, 'voided'),
    voidedAt: row.voided_at ?? null,
    voidReason: row.void_reason ?? null,
  }
}

// ── Resultados guardados (foto agregada por unidad) ─────────────────────

export interface LevelCounts {
  bajo: number
  medio: number
  alto: number
}

export interface ResultDistributions {
  dimensions: Record<string, LevelCounts>
  subdimensions: Record<string, LevelCounts>
  global: LevelCounts
}

export interface PsicosocialResultRow {
  id: string
  assessmentId: string
  unitId: string
  unitName: string
  headcount: number
  respondents: number
  suppressed: boolean
  minGroup: number
  instrumentId: string
  instrumentVersion: string
  distributions: ResultDistributions | null
  computedAt: string
}

function isPlainObjectResult(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function requireLevelCounts(value: unknown, field: string): LevelCounts {
  if (!isPlainObjectResult(value)) {
    throw new Error(`mapResult: falta o es inválido el campo "${field}"`)
  }
  const raw = value as Record<string, unknown>
  const levels: (keyof LevelCounts)[] = ['bajo', 'medio', 'alto']
  const result: any = {}
  for (let i = 0; i < levels.length; i++) {
    const level = levels[i]
    const n = raw[level]
    if (typeof n !== 'number' || !Number.isInteger(n) || n < 0) {
      throw new Error(`mapResult: "${field}.${level}" debe ser un entero >= 0, recibió: ${JSON.stringify(n)}`)
    }
    result[level] = n
  }
  return result as LevelCounts
}

// Exige al menos una clave, y que la suma bajo+medio+alto de CADA clave
// coincida con respondents (un cuestionario por persona, cada uno
// clasifica en exactamente un nivel por dimensión/subdimensión).
function requireLevelCountsMap(value: unknown, field: string, respondents: number): Record<string, LevelCounts> {
  if (!isPlainObjectResult(value)) {
    throw new Error(`mapResult: falta o es inválido el campo "${field}"`)
  }
  const raw = value as Record<string, unknown>
  const keys = Object.keys(raw)
  if (keys.length === 0) {
    throw new Error(`mapResult: "${field}" no puede estar vacío`)
  }
  const result: Record<string, LevelCounts> = {}
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]
    const counts = requireLevelCounts(raw[key], `${field}.${key}`)
    const sum = counts.bajo + counts.medio + counts.alto
    if (sum !== respondents) {
      throw new Error(
        `mapResult: Resultado inconsistente: la suma de "${field}.${key}" (${sum}) no coincide con respondents (${respondents})`
      )
    }
    result[key] = counts
  }
  return result
}

function requireIntField(value: unknown, field: string, min: number): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < min) {
    throw new Error(`mapResult: "${field}" debe ser un entero >= ${min}, recibió: ${JSON.stringify(value)}`)
  }
  return value
}

function requireBooleanField(value: unknown, field: string): boolean {
  if (typeof value !== 'boolean') {
    throw new Error(`mapResult: "${field}" debe ser un booleano, recibió: ${JSON.stringify(value)}`)
  }
  return value
}

// Pura: valida la FORMA de lo que ya calculó psychosocial_close_assessment
// (migración 20260920000600) — no recalcula nada. headcount, respondents
// y minGroup se validan como espejo de los CHECK de la tabla
// psychosocial_results. suppressed debe coincidir exactamente con
// respondents < minGroup, y con si distributions viene o no (igual que
// los CHECK de la tabla). En la rama no suprimida, cada dimensión, cada
// subdimensión y el global deben sumar exactamente respondents.
export function mapResult(row: any): PsicosocialResultRow {
  const id = required(row.id, 'id')
  const assessmentId = required(row.assessment_id, 'assessment_id')
  const unitId = required(row.unit_id, 'unit_id')
  const unitName = required(row.unit_name, 'unit_name')
  const headcount = requireIntField(row.headcount, 'headcount', 1)
  const respondents = requireIntField(row.respondents, 'respondents', 0)
  const suppressed = requireBooleanField(row.suppressed, 'suppressed')
  const minGroup = requireIntField(row.min_group, 'min_group', PSICOSOCIAL_MIN_GROUP_SIZE)
  const instrumentId = required(row.instrument_id, 'instrument_id')
  const instrumentVersion = required(row.instrument_version, 'instrument_version')
  const computedAt = required(row.computed_at, 'computed_at')

  if (suppressed !== (respondents < minGroup)) {
    throw new Error('mapResult: suppressed no coincide con respondents y minGroup')
  }

  if (suppressed === true) {
    if (row.distributions !== null && row.distributions !== undefined) {
      throw new Error('mapResult: una unidad suprimida no debe tener distributions')
    }
    return {
      id,
      assessmentId,
      unitId,
      unitName,
      headcount,
      respondents,
      suppressed,
      minGroup,
      instrumentId,
      instrumentVersion,
      distributions: null,
      computedAt,
    }
  }

  if (!isPlainObjectResult(row.distributions)) {
    throw new Error('mapResult: una unidad no suprimida debe tener distributions')
  }
  const rawDistributions = row.distributions as Record<string, unknown>
  const global = requireLevelCounts(rawDistributions.global, 'distributions.global')
  const dimensions = requireLevelCountsMap(rawDistributions.dimensions, 'distributions.dimensions', respondents)
  const subdimensions = requireLevelCountsMap(rawDistributions.subdimensions, 'distributions.subdimensions', respondents)

  const globalSum = global.bajo + global.medio + global.alto
  if (globalSum !== respondents) {
    throw new Error(
      `mapResult: Resultado inconsistente: la suma global (${globalSum}) no coincide con respondents (${respondents})`
    )
  }

  return {
    id,
    assessmentId,
    unitId,
    unitName,
    headcount,
    respondents,
    suppressed,
    minGroup,
    instrumentId,
    instrumentVersion,
    distributions: { dimensions, subdimensions, global },
    computedAt,
  }
}

// ── Evaluaciones ─────────────────────────────────────────────────────────

const ASSESSMENT_COLUMNS =
  'id, company_id, title, instrument_id, instrument_version, item_count, status, opened_at, closed_at, created_at, due_at, retention_days, contact_email, contact_phone, responses_purged_at'

function validateRetentionDaysOrThrow(value: number): void {
  if (!Number.isInteger(value) || value < MIN_RETENTION_DAYS || value > MAX_RETENTION_DAYS) {
    throw new Error(
      `retentionDays inválido: ${value} (debe ser un entero entre ${MIN_RETENTION_DAYS} y ${MAX_RETENTION_DAYS})`
    )
  }
}

// La conversión de la hora local a UTC debe hacerse en la pantalla
// (new Date(valorLocal).toISOString()) — esta capa nunca adivina la
// zona horaria: exige que el texto ya la incluya explícitamente.
function normalizeOptionalDueAt(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/.test(value)) {
    throw new Error(`dueAt inválido: "${value}" (debe incluir zona horaria explícita, por ejemplo "Z" o "-05:00")`)
  }
  const ms = Date.parse(value)
  if (Number.isNaN(ms)) {
    throw new Error(`dueAt inválido: "${value}" (no es una fecha reconocible)`)
  }
  return new Date(ms).toISOString()
}

function normalizeOptionalContactEmail(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null
  const trimmed = value.trim()
  if (trimmed.length === 0) return null
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
    throw new Error(`contactEmail inválido: "${value}"`)
  }
  return trimmed
}

function normalizeOptionalContactPhone(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null
  const trimmed = value.trim()
  if (trimmed.length === 0) return null
  if (trimmed.length < 6 || trimmed.length > 30) {
    throw new Error(`contactPhone inválido: "${value}" (debe tener entre 6 y 30 caracteres)`)
  }
  return trimmed
}

export async function fetchAssessments(companyId: string): Promise<PsicosocialAssessment[]> {
  if (!companyId) return []
  const { data, error } = await supabase
    .from('psychosocial_assessments')
    .select(ASSESSMENT_COLUMNS)
    .eq('company_id', companyId)
    .order('created_at', { ascending: false })
  throwIfError(error)
  return (data ?? []).map(mapAssessment)
}

export async function createAssessment(input: {
  companyId: string
  title: string
  dueAt?: string | null
  retentionDays?: number
  contactEmail?: string | null
  contactPhone?: string | null
}): Promise<string> {
  if (input.title.trim().length === 0) {
    throw new Error('createAssessment: title no puede estar vacío')
  }
  if (input.retentionDays !== undefined) {
    validateRetentionDaysOrThrow(input.retentionDays)
  }
  const dueAt = normalizeOptionalDueAt(input.dueAt)
  const contactEmail = normalizeOptionalContactEmail(input.contactEmail)
  const contactPhone = normalizeOptionalContactPhone(input.contactPhone)

  const payload: Record<string, unknown> = { company_id: input.companyId, title: input.title.trim() }
  if (dueAt !== null) payload.due_at = dueAt
  if (input.retentionDays !== undefined) payload.retention_days = input.retentionDays
  if (contactEmail !== null) payload.contact_email = contactEmail
  if (contactPhone !== null) payload.contact_phone = contactPhone

  const { data, error } = await supabase
    .from('psychosocial_assessments')
    .insert(payload)
    .select('id')
    .single()
  throwIfError(error)
  return required((data as any)?.id, 'id')
}

// El estado (borrador/abierta/cerrada) YA NO se cambia con UPDATE
// directo (migración 20260920000600 revoca ese permiso): pasa por estas
// dos funciones RPC, que además validan las precondiciones del lado de
// la base (códigos generados, fecha límite y contacto presentes, etc.)
// y sus mensajes de error llegan tal cual al usuario.
export async function openAssessment(assessmentId: string): Promise<void> {
  if (!assessmentId) {
    throw new Error('openAssessment: assessmentId no puede estar vacío')
  }
  const { data, error } = await supabase.rpc('psychosocial_open_assessment', {
    p_assessment_id: assessmentId,
  })
  throwIfError(error)
  if (data !== true) {
    throw new Error('No se pudo abrir la evaluación')
  }
}

export async function closeAssessment(
  assessmentId: string
): Promise<{ units: number; suppressedUnits: number }> {
  if (!assessmentId) {
    throw new Error('closeAssessment: assessmentId no puede estar vacío')
  }
  const { data, error } = await supabase.rpc('psychosocial_close_assessment', {
    p_assessment_id: assessmentId,
  })
  throwIfError(error)
  const result = data as any
  if (
    !result ||
    typeof result !== 'object' ||
    result.ok !== true ||
    typeof result.units !== 'number' ||
    !Number.isInteger(result.units) ||
    result.units < 0 ||
    typeof result.suppressed_units !== 'number' ||
    !Number.isInteger(result.suppressed_units) ||
    result.suppressed_units < 0
  ) {
    throw new Error('Respuesta inesperada al cerrar la evaluación')
  }
  return { units: result.units, suppressedUnits: result.suppressed_units }
}

// La base (psychosocial_assessment_guard, migración 20260920000500)
// rechaza cambios de fecha límite y de contacto una vez abierta la
// evaluación, y solo permite acortar el plazo de conservación — esos
// mensajes de error llegan tal cual al usuario, sin traducirlos.
export async function updateAssessmentSettings(input: {
  assessmentId: string
  dueAt: string | null
  retentionDays: number
  contactEmail: string | null
  contactPhone: string | null
}): Promise<void> {
  validateRetentionDaysOrThrow(input.retentionDays)
  const dueAt = normalizeOptionalDueAt(input.dueAt)
  const contactEmail = normalizeOptionalContactEmail(input.contactEmail)
  const contactPhone = normalizeOptionalContactPhone(input.contactPhone)

  const { data, error } = await supabase
    .from('psychosocial_assessments')
    .update({
      due_at: dueAt,
      retention_days: input.retentionDays,
      contact_email: contactEmail,
      contact_phone: contactPhone,
    })
    .eq('id', input.assessmentId)
    .select('id')
  throwIfError(error)
  if (!data || data.length === 0) {
    throw new Error('No se pudo guardar: sin permiso o la evaluación no existe')
  }
}

// Funciones puras (sin acceso a red ni a Date.now(): la fecha actual se
// recibe siempre por parámetro) para mostrar el estado de la fecha
// límite y el plazo de conservación en la UI.

export function deadlineState(
  dueAt: string | null,
  now: Date
): { state: 'sin_fecha' } | { state: 'vigente'; msLeft: number } | { state: 'vencida' } {
  if (dueAt === null) return { state: 'sin_fecha' }
  const dueMs = Date.parse(dueAt)
  if (Number.isNaN(dueMs)) {
    throw new Error(`deadlineState: dueAt inválido: "${dueAt}"`)
  }
  const msLeft = dueMs - now.getTime()
  if (msLeft <= 0) return { state: 'vencida' }
  return { state: 'vigente', msLeft }
}

const MS_PER_DAY = 86400000

export function erasureInfo(
  closedAt: string | null,
  retentionDays: number,
  now: Date
): null | { erasureAt: string; daysLeft: number } {
  if (closedAt === null) return null
  validateRetentionDaysOrThrow(retentionDays)
  const closedMs = Date.parse(closedAt)
  if (Number.isNaN(closedMs)) {
    throw new Error(`erasureInfo: closedAt inválido: "${closedAt}"`)
  }
  const erasureAtMs = closedMs + retentionDays * MS_PER_DAY
  const erasureAt = new Date(erasureAtMs).toISOString()
  const daysLeft = Math.max(0, Math.ceil((erasureAtMs - now.getTime()) / MS_PER_DAY))
  return { erasureAt, daysLeft }
}

// ── Unidades ─────────────────────────────────────────────────────────────

export async function fetchUnits(assessmentId: string): Promise<PsicosocialUnit[]> {
  if (!assessmentId) return []
  const { data, error } = await supabase
    .from('psychosocial_units')
    .select('id, assessment_id, name, headcount')
    .eq('assessment_id', assessmentId)
    .order('name')
  throwIfError(error)
  return (data ?? []).map(mapUnit)
}

export async function createUnit(input: { assessmentId: string; name: string; headcount: number }): Promise<void> {
  if (input.name.trim().length === 0) {
    throw new Error('createUnit: name no puede estar vacío')
  }
  if (!Number.isInteger(input.headcount) || input.headcount < 1) {
    throw new Error(`createUnit: headcount inválido: ${input.headcount} (debe ser un entero >= 1)`)
  }
  const { error } = await supabase
    .from('psychosocial_units')
    .insert({ assessment_id: input.assessmentId, name: input.name.trim(), headcount: input.headcount })
  throwIfError(error)
}

// ── Códigos de acceso ────────────────────────────────────────────────────

const HASH_REGEX = /^[0-9a-f]{64}$/

export async function addAccessCodes(unitId: string, hashes: string[]): Promise<number> {
  if (hashes.length < 1) {
    throw new Error('addAccessCodes: hashes debe tener al menos 1 elemento')
  }
  for (let i = 0; i < hashes.length; i++) {
    if (!HASH_REGEX.test(hashes[i])) {
      throw new Error(`addAccessCodes: hash inválido en la posición ${i}: "${hashes[i]}"`)
    }
  }
  const { data, error } = await supabase.rpc('psychosocial_add_codes', {
    p_unit_id: unitId,
    p_hashes: hashes,
  })
  throwIfError(error)
  return data as number
}

export async function voidUnusedCodes(unitId: string): Promise<number> {
  const { data, error } = await supabase.rpc('psychosocial_void_unused_codes', { p_unit_id: unitId })
  throwIfError(error)
  return data as number
}

export async function fetchCodeStats(assessmentId: string): Promise<UnitCodeStat[]> {
  if (!assessmentId) return []
  const { data, error } = await supabase.rpc('psychosocial_code_stats', { p_assessment_id: assessmentId })
  throwIfError(error)
  return (data ?? []).map(mapCodeStat)
}

// ── Resultados ───────────────────────────────────────────────────────────

const RESULT_COLUMNS =
  'id, assessment_id, unit_id, unit_name, headcount, respondents, suppressed, min_group, instrument_id, instrument_version, distributions, computed_at'

export async function fetchResults(assessmentId: string): Promise<PsicosocialResultRow[]> {
  if (!assessmentId) return []
  const { data, error } = await supabase
    .from('psychosocial_results')
    .select(RESULT_COLUMNS)
    .eq('assessment_id', assessmentId)
    .order('unit_name', { ascending: true })
  throwIfError(error)
  return (data ?? []).map(mapResult)
}

// ── Programa de prevención ───────────────────────────────────────────────

export async function fetchCycles(companyId: string): Promise<ProgramCycle[]> {
  if (!companyId) return []
  const { data, error } = await supabase
    .from('psychosocial_program_cycles')
    .select('id, company_id, program_year, status, closed_at, created_at')
    .eq('company_id', companyId)
    .order('program_year', { ascending: false })
  throwIfError(error)
  return (data ?? []).map(mapCycle)
}

export async function fetchCycleActivities(cycleId: string): Promise<ProgramActivityRow[]> {
  if (!cycleId) return []
  const { data, error } = await supabase
    .from('psychosocial_program_activities')
    .select('id, cycle_id, activity_no, planned_months, numerator, denominator, notes, updated_at')
    .eq('cycle_id', cycleId)
    .order('activity_no')
  throwIfError(error)
  return (data ?? []).map(mapActivity)
}

export async function fetchCycleEvidence(cycleId: string): Promise<ProgramEvidenceRow[]> {
  if (!cycleId) return []
  const { data, error } = await supabase
    .from('psychosocial_program_evidence')
    .select('id, cycle_id, activity_id, kind, storage_path, file_name, file_size, mime_type, uploaded_at, voided, voided_at, void_reason')
    .eq('cycle_id', cycleId)
    .order('uploaded_at', { ascending: true })
  throwIfError(error)
  return (data ?? []).map(mapEvidence)
}

export async function createCycle(companyId: string, year: number): Promise<string> {
  const { data, error } = await supabase.rpc('psychosocial_create_cycle', {
    p_company_id: companyId,
    p_year: year,
  })
  throwIfError(error)
  return data as string
}

export async function updateActivity(input: {
  activityId: string
  plannedMonths: number[]
  numerator: number | null
  denominator: number | null
  notes: string | null
}): Promise<void> {
  const { error } = await supabase.rpc('psychosocial_update_activity', {
    p_activity_id: input.activityId,
    p_planned_months: input.plannedMonths,
    p_numerator: input.numerator,
    p_denominator: input.denominator,
    p_notes: input.notes,
  })
  throwIfError(error)
}

export async function closeCycle(cycleId: string): Promise<void> {
  const { error } = await supabase.rpc('psychosocial_close_cycle', { p_cycle_id: cycleId })
  throwIfError(error)
}

export async function registerEvidence(input: {
  activityId: string
  kind: string
  storagePath: string
  fileSize: number
  mimeType: string
}): Promise<string> {
  const { data, error } = await supabase.rpc('psychosocial_register_evidence', {
    p_activity_id: input.activityId,
    p_kind: input.kind,
    p_storage_path: input.storagePath,
    p_file_size: input.fileSize,
    p_mime_type: input.mimeType,
  })
  throwIfError(error)
  return data as string
}

export async function voidEvidence(evidenceId: string, reason: string): Promise<void> {
  if (reason.trim().length === 0) {
    throw new Error('voidEvidence: reason no puede estar vacío')
  }
  const { error } = await supabase.rpc('psychosocial_void_evidence', {
    p_evidence_id: evidenceId,
    p_reason: reason,
  })
  throwIfError(error)
}

// ── Almacenamiento (Storage) ─────────────────────────────────────────────

const EVIDENCE_BUCKET = 'psychosocial-evidence'
const MAX_EVIDENCE_FILE_SIZE = 10485760
const ALLOWED_EVIDENCE_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png']

function assertNoSlash(value: string, field: string): void {
  if (value.length === 0 || value.indexOf('/') !== -1) {
    throw new Error(`buildEvidencePath: "${field}" no puede estar vacío ni contener '/': "${value}"`)
  }
}

export function buildEvidencePath(input: {
  companyId: string
  cycleId: string
  activityId: string
  uniqueId: string
  fileName: string
}): string {
  assertNoSlash(input.companyId, 'companyId')
  assertNoSlash(input.cycleId, 'cycleId')
  assertNoSlash(input.activityId, 'activityId')
  assertNoSlash(input.uniqueId, 'uniqueId')

  const safeName = nombreSeguroStorage(input.fileName)
  return `${input.companyId}/${input.cycleId}/${input.activityId}/${input.uniqueId}-${safeName}`
}

export async function uploadEvidenceFile(path: string, file: File): Promise<void> {
  if (file.size < 1 || file.size > MAX_EVIDENCE_FILE_SIZE) {
    throw new Error(`uploadEvidenceFile: tamaño de archivo inválido: ${file.size} bytes (debe estar entre 1 y ${MAX_EVIDENCE_FILE_SIZE})`)
  }
  if (ALLOWED_EVIDENCE_MIME_TYPES.indexOf(file.type) === -1) {
    throw new Error(`uploadEvidenceFile: tipo de archivo no permitido: "${file.type}" (debe ser application/pdf, image/jpeg o image/png)`)
  }
  const { error } = await supabase.storage
    .from(EVIDENCE_BUCKET)
    .upload(path, file, { upsert: false, contentType: file.type })
  throwIfError(error)
}

export async function createEvidenceSignedUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from(EVIDENCE_BUCKET)
    .createSignedUrl(path, 60)
  throwIfError(error)
  return required((data as any)?.signedUrl, 'signedUrl')
}
